import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { FileText, ExternalLink, BookOpen } from 'lucide-react';

const PaperReferencesSlide = () => {
  const coreDeepSeek = [
    { title: 'DeepSeek-R1: Incentivizing Reasoning Capability in LLMs via RL', arxiv: '2501.12948', date: 'Jan 2025' },
    { title: 'DeepSeek-V3 Technical Report', arxiv: '2412.19437', date: 'Dec 2024' },
    { title: 'DeepSeekMath: Pushing the Limits of Mathematical Reasoning', arxiv: '2402.03300', date: 'Feb 2024' },
  ];
  
  const foundational = [
    { title: "Let's Verify Step by Step (OpenAI PRMs)", arxiv: '2305.20050', date: 'May 2023' },
    { title: 'STaR: Bootstrapping Reasoning With Reasoning', arxiv: '2203.14465', date: 'Mar 2022' },
    { title: 'Chain-of-Thought Prompting Elicits Reasoning (Wei et al.)', arxiv: '2201.11903', date: 'Jan 2022' },
    { title: 'Proximal Policy Optimization Algorithms (PPO)', arxiv: '1707.06347', date: 'Jul 2017' },
  ];
  
  const related = [
    { title: 'Phi-4 Technical Report (Microsoft)', arxiv: '2412.08905', date: 'Dec 2024' },
    { title: 'Qwen2.5-Math: Toward Mathematical Expert Model', arxiv: '2409.12122', date: 'Sep 2024' },
    { title: 'Scaling LLM Test-Time Compute Optimally', arxiv: '2408.03314', date: 'Aug 2024' },
  ];

  const PaperList = ({ papers, color }) => (
    <ul className="space-y-2">
      {papers.map((paper, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <FileText className={`w-4 h-4 mt-0.5 flex-shrink-0 text-${color}`} />
          <div className="flex-1">
            <span className="text-foreground">{paper.title}</span>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-xs px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">
                {paper.arxiv}
              </code>
              <span className="text-xs text-muted-foreground">{paper.date}</span>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Key papers for understanding DeepSeek's reasoning breakthroughs"
      >
        Paper References
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Core DeepSeek */}
          <div className="slide-card border-primary/30">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-primary" />
              <h3 className="font-heading font-semibold text-foreground">Core DeepSeek Papers</h3>
            </div>
            <PaperList papers={coreDeepSeek} color="primary" />
          </div>
          
          {/* Foundational */}
          <div className="slide-card border-secondary/30">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-secondary" />
              <h3 className="font-heading font-semibold text-foreground">Foundational Papers</h3>
            </div>
            <PaperList papers={foundational} color="secondary" />
          </div>
          
          {/* Related Work */}
          <div className="slide-card border-accent/30">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-accent" />
              <h3 className="font-heading font-semibold text-foreground">Related Work</h3>
            </div>
            <PaperList papers={related} color="accent" />
          </div>
        </div>
        
        {/* Official Resources */}
        <div className="mt-6 slide-card">
          <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-primary" />
            Official Resources
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'GitHub Repository', url: 'github.com/deepseek-ai/DeepSeek-R1' },
              { name: 'R1 Model (HF)', url: 'huggingface.co/deepseek-ai/DeepSeek-R1' },
              { name: 'Distilled Models', url: 'huggingface.co/collections/deepseek-ai' },
              { name: 'API Docs', url: 'api-docs.deepseek.com' },
            ].map((resource, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                <p className="text-sm font-semibold text-foreground mb-1">{resource.name}</p>
                <p className="text-xs text-muted-foreground font-mono truncate">{resource.url}</p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
          <p className="text-sm text-muted-foreground text-center">
            <strong className="text-primary">Open Release:</strong> DeepSeek-R1 models, training methodology, and 800K distillation samples released under MIT license.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default PaperReferencesSlide;
